import Vue from 'vue';
import ElementUI from 'element-ui';
import 'element-ui/lib/theme-chalk/index.css';
import './styles/global.css';  // 全局样式
import App from './App.vue';
import router from './router';
import axios from 'axios';

Vue.use(ElementUI, { size: 'small' });

// ==== Element UI 2.x clickoutside.js 已知 bug 兜底（DOM 原型层）====
// 现象：Cannot read properties of null (reading 'setAttribute')
//   at removeTabindex (element-ui.common.js) → _hide → clickoutside.js documentHandler → forEach
// 成因：当 BaseModal / el-dialog 等 v-if 卸载销毁 element-ui 子组件时，element-ui 的
//   clickoutside.js 仍持有已 destroy 的 VueComponent $el 引用；下一次点击 document，
//   element-ui 遍历到该 null 引用，调用 el.setAttribute('tabindex','-1') 抛 TypeError。
// 关键洞察：这个错是 document click 监听器里抛的，**不在 Vue 渲染链路里**，
//   Vue.config.errorHandler 收不到（这就是之前改 main.js errorHandler 没生效的原因）。
//   vue-cli@5 的 runtime-error-overlay 会自己监听 window.error 全屏显示。
// 真正的根治：在 DOM 原型层 monkey-patch setAttribute，receiver 为 null 时静默返回。
//   这样 element-ui clickoutside.js 调用时根本不会抛错，错误冒泡链就消失了。
// 该 patch 在 import 时即安装，早于 element-ui 的 directive 首次被任何组件 used 时
//   的 clickoutside 注册逻辑，所以最早一次 click 也已经覆盖。
(function patchDomSetAttributeForElementUIBug() {
  try {
    const proto = typeof Element !== 'undefined' ? Element.prototype : null;
    if (!proto || proto.__productClientNullSafePatch) return;
    const desc = Object.getOwnPropertyDescriptor(proto, 'setAttribute');
    if (!desc || typeof desc.value !== 'function') return;
    const origSet = desc.value;
    function patchedSetAttribute(name, value) {
      // 关键：receiver 为 null/undefined 静默返回 → element-ui clickoutside 已知 bug 触发链直接断
      if (this == null) return undefined;
      try {
        return origSet.apply(this, arguments);
      } catch (e) {
        // 接收者存在但调用抛错：识别 element-ui 已知栈后静默；其余真实错误照常抛出
        try {
          const st = String((e && e.stack) || e || '');
          if (
            st.includes('removeTabindex') ||
            st.includes('clickoutside') ||
            (st.includes("reading 'setAttribute'") && st.includes('element-ui'))
          ) {
            // eslint-disable-next-line no-console
            console.warn('[silenced] element-ui clickoutside null.setAttribute:', st.split('\n')[0]);
            return undefined;
          }
        } catch (_) { }
        throw e;
      }
    }
    Object.defineProperty(proto, 'setAttribute', {
      configurable: true,
      writable: true,
      enumerable: desc.enumerable,
      value: patchedSetAttribute,
    });
    proto.__productClientNullSafePatch = true;
    // eslint-disable-next-line no-console
    console.info('[宁易租] element-ui clickoutside null.setAttribute 兜底已安装 (v2)');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[宁易租] DOM setAttribute 兜底安装失败（不影响业务）', e);
  }
})();
// ==== 兜底结束 ====

// 开发环境：走 vue.config.js 代理（/api -> localhost:3000）
// 生产环境：直接调用后端 product.wxkcgb.top（后端已配 CORS 允许跨域）
const isProd = process.env.NODE_ENV === 'production';
const API_BASE = isProd
  ? 'https://product.wxkcgb.top/api'  // 生产：直接调后端域名
  : '/api'; // 开发：本地代理

const http = axios.create({ baseURL: API_BASE });
Vue.prototype.$http = http;
Vue.config.productionTip = false;

// ==== Element UI 2.x clickoutside.js 已知 bug 兜底 ====
// 现象：Cannot read properties of null (reading 'setAttribute')
//   at removeTabindex (element-ui.common.js)
//   at _hide → clickoutside.js documentHandler → Array.forEach
// 成因：当弹窗（BaseModal / el-dialog 等）被 v-if 销毁时，element-ui 内部
//   clickoutside.js 还持有已卸载组件的 DOM 引用；下一次点击 document 时，
//   仍 try 访问该组件实例的 $el，对已为 null 的节点 setAttribute('tabindex','-1') 报错。
// 业务影响：无（仅控制台/红框刷屏，但生产红框报错对客户不友好）。
// 修法：识别 element-ui 内部这种已知 null.setAttribute / clickoutside 栈，
//   静默 swallow；其余真错误仍正常抛出。
function _isElementUIClickoutsideBug(err) {
  const msg = String((err && (err.stack || err.message)) || err || '');
  if (!msg) return false;
  if (!msg.includes('element-ui')) return false;
  // 命中其一即可：clickoutside 的引用 → 试图 setAttribute(null, 'tabindex', '-1')
  if (msg.includes("reading 'setAttribute'")) return true;
  if (msg.includes('removeTabindex')) return true;
  if (msg.includes('clickoutside.js')) return true;
  return false;
}
const _origErrorHandler = Vue.config.errorHandler;
Vue.config.errorHandler = function (err, vm, info) {
  if (_isElementUIClickoutsideBug(err)) {
    // 仅 console.warn 留痕，不再冒泡到 Vue 错误处理（避免红框 + 不影响监控正常错误）
    // eslint-disable-next-line no-console
    console.warn('[silenced] element-ui internal clickoutside bug:', info, String(err && err.message || err));
    return;
  }
  if (typeof _origErrorHandler === 'function') return _origErrorHandler.call(this, err, vm, info);
  // eslint-disable-next-line no-console
  console.error('[Vue error]', info, err);
};
// 同源问题：极少数情况下 element-ui 这个错会从 window.onerror 冒泡，也吞掉
window.addEventListener('error', function (e) {
  if (e && e.error && _isElementUIClickoutsideBug(e.error)) {
    e.preventDefault();
    e.stopImmediatePropagation && e.stopImmediatePropagation();
  }
}, true);
// ==== 兜底结束 ====

// 视频缓存破坏：视频文件上传后会经后端 ffmpeg 异步压缩覆盖原文件（同名替换），
// 浏览器可能缓存到压缩前的旧版本。此方法在视频 URL 后追加版本参数强制拉取最新版。
// 策略：按天变化（每天首次访问时刷新），配合后端 ETag 机制保证文件覆盖后能拿到新版。
Vue.prototype.$videoSrc = function (url) {
  if (!url) return '';
  // 已带查询参数的 URL 用 & 拼接，否则用 ?
  const sep = url.includes('?') ? '&' : '?';
  // 按天变化：当天内同一用户共享缓存，跨天后自动刷新
  const ver = String(Math.floor(Date.now() / 86400000)); // 天级时间戳
  return url + sep + '_v=' + ver;
};

// 列表封面图：把大图交给后端 /api/thumb 按需缩放成小封面（带磁盘缓存，覆盖/等比裁切）。
// 仅对本地 /uploads/ 资源生效；非本地资源退化为 $videoSrc。详情/灯箱仍用 $videoSrc 看原图。
Vue.prototype.$thumb = function (url, w, h) {
  if (!url) return '';
  if (!/^\/uploads\//.test(url)) return this.$videoSrc(url);
  const qs = ['src=' + encodeURIComponent(url)];
  if (w) qs.push('w=' + w);
  if (h) qs.push('h=' + h);
  return '/api/thumb?' + qs.join('&');
};

// 图片资源原图直链：把 /uploads/xxx 改写到 /api/uploads/xxx。
//   生产 Nginx 通常反代了 /api（API + 现在的 /api/uploads），但不一定反代了 /uploads。
//   弹窗图片、富文本内嵌图、设备档案原图、扫码页详情等都用这个方法，
//   与 /api/thumb 一样基于 /api 代理，与 Nginx 配置解耦。
//   非 /uploads/ 资源退化为 $videoSrc。
Vue.prototype.$uploadSrc = function (url) {
  if (!url) return '';
  // if (/^\/uploads\//.test(url)) return '/api' + url;
  if (/^\/uploads\//.test(url)) return url;
  return this.$videoSrc(url);
};

// 公开路径（移动端扫码/公开查看等）：不发鉴权 header + 401 不踢向 login。
// 二维码扫描进入的页面必须在 publicPaths 里，否则会被 401 拦截器自动跳转登录页。
const PUBLIC_PATHS = ['/m/scan', '/m/device'];

function isOnPublicRoute() {
  // router 已经 import 过了；用当前路径前缀判断（兼容 /m/device/1 这种深链）
  const cur = (router.currentRoute && router.currentRoute.path) || '';
  return PUBLIC_PATHS.some(p => cur === p || cur.indexOf(p + '/') === 0);
}

// 请求拦截：自动带 token。
// 设计原则：前端「始终」附带 token（localStorage 有就发），鉴权由【后端 PUBLIC_PATHS 白名单】权威裁决。
// 公开接口（移动端扫码/公开详情）后端根本不校验 token，前端发了也无害；
// 受保护接口必须带 token。这样彻底消除「前端手维护列表漏发 token → 401 被踢」这一整类 bug。
http.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers['Authorization'] = 'Bearer ' + token;
  return config;
});

// 响应拦截：统一错误处理（公开路径出现 401 时不踢去 login，仅提示）
http.interceptors.response.use(
  res => {
    // 后端在令牌宽限期内静默续期，会带 X-New-Token 响应头，前端无感续接，避免被踢。
    const nt = res.headers && res.headers['x-new-token'];
    if (nt) {
      try { localStorage.setItem('token', nt); } catch (e) { /* ignore */ }
    }
    return res.data; // 直接返回 data 部分
  },
  err => {
    const { response, message: netMsg } = err;
    // 网络错误
    if (!response) {
      if (!isOnPublicRoute()) Vue.prototype.$message.error('网络连接失败，请检查网络');
      return Promise.reject(err);
    }
    // 401：未登录（排除登录页本身 + 公开页面）
    if (response.status === 401 && router.currentRoute.path !== '/login' && !isOnPublicRoute()) {
      localStorage.clear();
      Vue.prototype.$message.error('登录已过期，请重新登录');
      router.push('/login');
      return Promise.reject(err);
    }
    // 公开路径的 401：仅提示，不踢
    if (response.status === 401 && isOnPublicRoute()) {
      console.warn('[公开页面] 接口暂不可用（401）', err);
      return Promise.reject(err);
    }
    // 其他业务错误：从响应体取 message，没有则给默认提示
    const msg = (response.data && response.data.message) || '操作失败，请稍后重试';
    // 不在登录页时弹提示（登录页自己处理错误）
    if (router.currentRoute.path !== '/login' && !isOnPublicRoute()) {
      Vue.prototype.$message.error(msg);
    }
    return Promise.reject(err);
  }
);

new Vue({ router, render: h => h(App) }).$mount('#app');
